/* eslint-disable react/require-default-props */
import { Button } from 'antd';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { ButtonSlide } from '@source/types/common';
import useMobileView from '@design-system/hooks/useMobileView';
import { useTranslation } from 'react-i18next';
import useRegionConfig from '@source/hooks/useRegionConfig';

interface IMobileWrapperProps {
  isShow: boolean;
}

const StyledTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;

  .left-btn {
    margin: 0 9px 0 19px;
  }
`;

const StyledTitleMobileWrapper = styled.div<IMobileWrapperProps>`
  display: ${(props) => (props.isShow ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  margin-top: 20px;
`;

const StyledDesktopTitle = styled.div`
  ${(props) => props.theme.typo.style.captionBold};
  font-size: 24px;
  line-height: 30px;
`;

const StyledMobileTitle = styled.div`
  ${(props) => props.theme.typo.familyGoogle.bold};
  font-size: 20px;
  margin-bottom: 16px;

  @media screen and (min-width: 992px) {
    font-size: 24px;
  }
`;

const StyledSubTitle = styled.div`
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-size: 12px;
  color: ${(props) => props.theme.color.onBackgroundMediumEmphasis};
`;

const StyledButton = styled(Button)`
  height: 32px !important;
  width: 32px !important;
  border-radius: 8px !important;
  background: ${(props) => props.theme.color.primaryBase};
  border: none;
  color: ${(props) => props.theme.color.background};
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
`;

const StyledTopLine = styled.div<{ country?: string }>`
  width: 65px;
  height: 5px;
  background: ${(props) =>
    ['my', 'hk'].includes(props.country || '') ? 'transparent' : props.theme.color.primaryBase};
  margin-bottom: 12px;
`;

const StyledPagination = styled.span`
  font-family: 'Poppins';
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${(props) => props.theme.color.onBackgroundHighEmphasis};
`;

interface CardSliderProps {
  slide?: (type: ButtonSlide) => void;
  pageIndex?: number;
  pageSize?: number;
  title: string | ReactNode;
  responsiveTitle: string | ReactNode;
  responsiveSubTitle: string | ReactNode;
}

const TitleHeader = ({ slide, pageIndex, pageSize, title, responsiveTitle, responsiveSubTitle }: CardSliderProps) => {
  const isResponsive = useMobileView(1280);
  const { t } = useTranslation();
  const {
    countryConfig: { country },
  } = useRegionConfig();

  const handleSlide = (type: ButtonSlide) => () => {
    if (slide) slide(type);
  };

  if (isResponsive) {
    return (
      <StyledTitleMobileWrapper isShow={!!responsiveTitle}>
        <StyledTopLine country={country} />
        <StyledMobileTitle>{responsiveTitle}</StyledMobileTitle>
        <StyledSubTitle>{responsiveSubTitle}</StyledSubTitle>
      </StyledTitleMobileWrapper>
    );
  }

  return (
    <StyledTitleWrapper>
      <StyledDesktopTitle>{title}</StyledDesktopTitle>

      {!!slide && (
        <div>
          <StyledPagination>{t('shared.common.of', { pageIndex, pageSize })}</StyledPagination>

          <StyledButton
            className="left-btn"
            type="primary"
            shape="default"
            icon={<LeftOutlined />}
            size="small"
            onClick={handleSlide('prev')}
          />
          <StyledButton
            type="primary"
            shape="default"
            icon={<RightOutlined />}
            size="small"
            onClick={handleSlide('next')}
          />
        </div>
      )}
    </StyledTitleWrapper>
  );
};

export default TitleHeader;
